/* planTimeline — Phase B helpers for the Doctor Self-Plan timeline.
 *
 * computeETA derives the projected case-completion date from:
 *   - plan.start_date              ISO yyyy-mm-dd; null when doctor leaves it TBD
 *   - max(aligner_count_upper, aligner_count_lower)   total stages (the longer arch leads)
 *   - plan.wear_schedule_default_days  base cadence per aligner
 *   - plan.wear_schedule_overrides     per-stage cadence overrides (sums into total days)
 *   - plan.phase_breaks                sum(pause_days) added between stages
 *
 * Returns:
 *   { eta: Date|null, total_treatment_days: number, total_pause_days: number,
 *     has_start_date: bool, has_min_inputs: bool, reason?: string }
 *
 * Pure function — no React, no API. Both the doctor self-plan editor and
 * the CoE-planned doctor's plan card consume it.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function computeETA(plan = {}) {
    const totalStages = Math.max(
        Number(plan.aligner_count_upper || 0),
        Number(plan.aligner_count_lower || 0),
    );
    const baseDays = Number(plan.wear_schedule_default_days || 0);
    const overrides = Array.isArray(plan.wear_schedule_overrides) ? plan.wear_schedule_overrides : [];
    const phases = Array.isArray(plan.phase_breaks) ? plan.phase_breaks : [];

    // Per-stage day cost: overrides win over the default.
    let totalDays = 0;
    if (totalStages > 0 && baseDays > 0) {
        const overrideByStage = new Map(
            overrides
                .filter((o) => o && Number.isFinite(Number(o.stage)) && Number.isFinite(Number(o.days)))
                .map((o) => [Number(o.stage), Number(o.days)]),
        );
        for (let s = 1; s <= totalStages; s++) {
            totalDays += overrideByStage.has(s) ? overrideByStage.get(s) : baseDays;
        }
    }

    // Phase break pauses ADD to the total — only count breaks whose
    // to_stage is within the planned stage count (defensive).
    let pauseDays = 0;
    for (const p of phases) {
        const ts = Number(p?.to_stage);
        const pd = Number(p?.pause_days);
        if (!Number.isFinite(ts) || !Number.isFinite(pd)) continue;
        if (totalStages > 0 && ts > totalStages) continue;
        if (pd > 0) pauseDays += pd;
    }

    const hasMinInputs = totalStages > 0 && baseDays > 0;
    const hasStartDate = !!plan.start_date;
    let eta = null;
    let reason;

    if (!hasMinInputs) {
        reason = "Add aligner counts and wear duration to compute ETA";
    } else if (!hasStartDate) {
        reason = `${totalDays + pauseDays} day plan · start date TBD`;
    } else {
        try {
            const start = new Date(plan.start_date + "T00:00:00Z");
            if (Number.isFinite(start.getTime())) {
                eta = new Date(start.getTime() + (totalDays + pauseDays) * MS_PER_DAY);
            } else {
                reason = "Invalid start date";
            }
        } catch {
            reason = "Invalid start date";
        }
    }

    return {
        eta,
        total_treatment_days: totalDays + pauseDays,
        total_pause_days: pauseDays,
        total_stages: totalStages,
        has_start_date: hasStartDate,
        has_min_inputs: hasMinInputs,
        reason,
    };
}

/* fmtETA — format the eta Date returned above into the IST-friendly
   short format used across the dashboard. Falls back to the reason
   string when no eta exists. */
export function fmtETA(result) {
    if (!result) return "—";
    if (result.eta) {
        try {
            return result.eta
                .toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                .replace(/ /g, "-");
        } catch { return "—"; }
    }
    return result.reason || "—";
}

/* defaultMilestonesForPlan — when the doctor first enables milestones we
   suggest one at the midpoint of treatment (rounded to a clean stage).
   The doctor can edit / delete / add more. Returns an empty array when
   we don't have enough info to suggest. */
export function defaultMilestonesForPlan(plan = {}) {
    const totalStages = Math.max(
        Number(plan.aligner_count_upper || 0),
        Number(plan.aligner_count_lower || 0),
    );
    if (totalStages < 4) return [];
    const mid = Math.max(2, Math.round(totalStages / 2));
    return [{ stage: mid, label: "Mid-treatment IPR & attachments review" }];
}
